import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import ReactSelect from "@src/common/ReactSelect";
import { restrictInvalidNumberKeys } from "@src/config/helper";
import { saveBankConfig, getBankConfig, getBankList,getMasterConfig, deleteBankRow } from "@src/store/slices/configPanel";
import { useLanguage } from "@src/context/LanguageContext";
import FullPageLoader from "@src/common/FullPageLoader";
import { toast } from "react-toastify";
import { STATUS_SUCCESS,INSURER_STATUS,ONLY_ALPHABET_REGEX } from "@src/config/constant";
import * as HELPER from "@src/config/helper";
import { headersData,bankFeesInitialList,BankFeesRow,OptionType,KEYS,REQUIRED_KEYS,DROPDOWN_KEYS,CURRENCY_KEYS, NUMBER_KEYS, DECIMAL_KEYS, DELETE_ROW, DELETE_CONFIRMATION, createEmptyBankFeesRow, DEFAULT_BANK_STATUS } from "./config/BankConfig";
import Pagination from "@src/common/Pagination";
import ConfirmPopup from "@src/common/ConfirmPopUp";


const Bank: React.FC<any> = (props) =>  {
  const { masterConfig } = props;
  const [rows, setRows] = useState(bankFeesInitialList);
  const [bankList, setBankList] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, any>[]>([]);
  const { translations } = useLanguage();
  const dispatch: any = useDispatch();
  const allRowsRef = useRef<{ [page: number]: BankFeesRow[] }>({});
  const pageRef = useRef(1);
  const [initialRowsData, setInitialRowsData] = useState<any>();

  const [masterConfigData, setMasterConfigData] = useState<any>(masterConfig);
  const [confirmation, setConfirmation] =useState<DELETE_ROW>({row:{},show:false,index:0});
  
  const fetchMasterConfig = async()=>{
    try {
        setLoading(true);
        const response = await dispatch(getMasterConfig());
        if(response?.status === STATUS_SUCCESS){
            setMasterConfigData(response?.data);
            await fetchBankList();
        }
    } catch (error) {
        console.error(error);
    }finally{
        setLoading(false);
    }
  }
  useEffect(() => {
    fetchMasterConfig();
  }, []);
  

  const partner: OptionType[] = masterConfigData?.partner_list || [];
  const insurerList: OptionType[] = masterConfigData?.insurer_list_with_partner_id || [];
  const paymentType: OptionType[] = [
    {
      value: 1,
      label: "Bulk Payment",
    },
    {
      value: 2,
      label: "Per Policy Payment",
    },
  ];
  const configStatus : OptionType[] = masterConfigData?.config_status || []
  const defaultStatusValue =
    (Array.isArray(configStatus) ? configStatus : []).find((option: any) => {
      const label = String(option?.label || "").trim().toLowerCase();
      return label === "active" || label === String(translations?.commonMsg?.active || "").trim().toLowerCase() || +option?.value === DEFAULT_BANK_STATUS;
    })?.value ?? DEFAULT_BANK_STATUS;
  

  const currentPage = pageRef.current;


  const handleChange = (event: any, key: string, index: number = 0) => {
    const isDropdown = DROPDOWN_KEYS.includes(key);
    let value: any = isDropdown ? event?.value : event?.target?.value;

    const updatedRows = [...rows.data];
    const row: any = { ...updatedRows[index] };

    if(CURRENCY_KEYS?.includes(key)) value = HELPER.removeCurrencyFormattingId(value);
    const isValid = handleChangeValidation(key, value, index, row);
    if (!isValid) return;

    if (key === KEYS.ONE) {
      row[KEYS.TWO] = "";
    }

    row[key] = value;
    updatedRows[index] = row;

    allRowsRef.current[currentPage] = updatedRows;

    setRows((prev) => ({
      ...prev,
      data: updatedRows,
    }));

    if ([KEYS.ONE, KEYS.TWO].includes(key)) setErrorsOfDuplicatesAcrossPages(currentPage, index);
  };
  const handleChangeValidation = (
    key: string,
    value: any,
    index: number = 0,
    row: Record<string, any>
  ): boolean => {
    const updatedErrors = { ...errors };
   
    if (!updatedErrors[currentPage]) updatedErrors[currentPage] = {};
    if (!updatedErrors[currentPage][index])
      updatedErrors[currentPage][index] = {};

    const setError = (msg: string) => {
      updatedErrors[currentPage][index][key] = msg;
    };

    const clearError = () => { 
    
      delete updatedErrors?.[currentPage]?.[index]?.[key];
      if (Object.keys(updatedErrors?.[currentPage]?.[index] || {})?.length === 0) {
        delete updatedErrors?.[currentPage]?.[index];
      }
    };

    
    if (
      REQUIRED_KEYS?.includes(key) && !DROPDOWN_KEYS.includes(key) &&
      (value === "" || value === null || value === undefined)
    ) {
        
      setError(translations?.commonMsg?.FIELD_CANNOT_BE_BLANK);
    }else if([KEYS.SEVEN,KEYS.EIGHT]?.includes(key)){
        const newValue = Number(value); 
        if(newValue < 0 || newValue > 90) setError(translations?.BANK?.ZERO_TO_NINETY_ALLOWED);
        else clearError();
    }
    else if(CURRENCY_KEYS.includes(key)){
        if(
          REQUIRED_KEYS?.includes(key) &&
          (value === "" || value === null || value === undefined)
        ) setError(translations?.commonMsg?.FIELD_CANNOT_BE_BLANK);
        else clearError();
    }
    else {
      clearError();
    }

    if(DECIMAL_KEYS?.includes(key)){
      const isValid = HELPER.isValidDecimal(value);
      if(!isValid)setError(translations?.commonMsg?.MAX_4D);
      else clearError();
    }

    setErrors(updatedErrors);
    return true;
  };
  const setErrorsOfDuplicatesAcrossPages = (
    currentPage: number,
    index: number
  ) => {
    const result = findExistingCombination(currentPage, index);

    const updatedErrors: any = { ...errors };
    if (!updatedErrors[currentPage]) updatedErrors[currentPage] = {};
    if (!updatedErrors[currentPage][index])
      updatedErrors[currentPage][index] = {};
    if (result) {
      const messageTemplate =
        translations?.BANK?.DUPLICATE_COMBINATION || "";
      const errorMessage = messageTemplate
        .replace("{{row}}", String(result.index + 1))
        .replace("{{page}}", String(result.page));
      updatedErrors[currentPage][index]["duplicate"] = errorMessage;
      
    } else {
      for (let [rowIndex, rowErrors] of Object.entries(
        updatedErrors[currentPage]
      )) {
        let row: any = rowErrors;
        if (row["duplicate"]) {
          delete updatedErrors[currentPage][rowIndex]["duplicate"];
        }
      }
    }

    setErrors(updatedErrors);
  };
  const findExistingCombination = (
    currentPage: number,
    currentIndex: number
  ) => {
    const allData = allRowsRef.current;
    const currentRow = allData?.[currentPage]?.[currentIndex];
    if (!currentRow) return null;

    const currentKey = `${currentRow.partner_id}-${currentRow.insurer_id}`;

    for (const [pageStr, rows] of Object.entries(allData)) {
      const page = parseInt(pageStr);
      for (let index = 0; index < rows.length; index++) {
        const row = rows[index];
        const comboKey = `${row.partner_id}-${row.insurer_id}`;

        if (
          comboKey === currentKey &&
          !(page === currentPage && index === currentIndex)
        ) {
          return { page, index };
        }
      }
    }

    return null;
  };
  const validateRowsBeforeSubmit = (rows: BankFeesRow[]): boolean => {
    const updatedErrors = { ...errors };
    let hasEmptyError = Object.values(updatedErrors).some((pageErrors) =>
      Object.values(pageErrors).some(
        (rowErrors) => Object.keys(rowErrors).length > 0
      )
    );

    if (hasEmptyError) return false;    

    if (!updatedErrors[currentPage]) updatedErrors[currentPage] = {};

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!updatedErrors[currentPage][i]) updatedErrors[currentPage][i] = {};

      for (const key of REQUIRED_KEYS) {
        const value = row[key];

        if (value === "" || value === null || value === undefined) {
          updatedErrors[currentPage][i][key] =
            translations?.commonMsg?.required;
        }
      }
    }

    setErrors(updatedErrors);
    hasEmptyError = Object.values(updatedErrors).some((pageErrors) =>
      Object.values(pageErrors).some(
        (rowErrors) => Object.keys(rowErrors).length > 0
      )
    );

    if (hasEmptyError) return false;
    return true;
  };
  const fetchData = (params: any) => {
    try {
      setLoading(true);
      dispatch(getBankConfig(params))
        .then((res: any) => {
          if (res && res.status === STATUS_SUCCESS) {
            let data: any = res || {};
  
            allRowsRef.current[pageRef.current] = data?.data;
            if(data?.data?.length > 0){
              setRows(data);
            }
            setInitialRowsData(data);
            setLoading(false);

          }
        })
      .catch((err: Error) => {});
      
    } catch (error) {
      console.error(error);
    }
  };
  const fetchBankList = async () => {
    try {
      setLoading(true);
      const res:Record<string,any> = await dispatch(getBankList());
      if(res.status===STATUS_SUCCESS) setBankList(res.data);
    }catch(error){
      console.error(error);
    }finally{
      setLoading(false);
    }
  }
  const handleSubmit = async () => {
    try {
        
        setLoading(true);
        const isValid = validateRowsBeforeSubmit(rows.data);
        if (!isValid) return;
      
        const initData = initialRowsData?.['data'];
        const changedData = rows?.data;
        let dataToBeSaved:any = HELPER?.getChangedRows(initData,changedData);        
        dataToBeSaved = dataToBeSaved.map((row: any) => {
    
         for(const key in row?.newRow){
          if(NUMBER_KEYS?.includes(key) && typeof row?.newRow[key] === 'string'){
            row.newRow[key] = Number(row?.newRow[key]);
          } 
          if(DECIMAL_KEYS?.includes(key) && typeof row?.newRow[key] === 'string'){
            row.newRow[key] = parseFloat(row?.newRow[key]);            
          }          
         }
         return row?.newRow
         
        })
        if(dataToBeSaved?.length === 0) {
          toast.error(translations?.commonMsg?.NO_CHANGE || translations?.commonMsg?.loading);
          return
        }
  
        const response: any = await dispatch(saveBankConfig( {data:dataToBeSaved} ));
        if (response.status === STATUS_SUCCESS) {
          const updatedInitRowsData = { ...initialRowsData };
          updatedInitRowsData.data = rows.data;
          setInitialRowsData(updatedInitRowsData)
          toast.success(response.message);
        } else {
          toast.error(response.message);
        }
    } catch (error) {
        console.error(error);
    }
    finally{
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData({ page_number: currentPage });
  }, []);

  useEffect(() => {
    const hasErrors = Object.keys(errors || {}).length > 0;
    if (hasErrors && rows.data.length > 0) {
      validateRowsBeforeSubmit(rows.data);
    }
  }, [translations]);
  const handlePageChange = (params: any) => {
    let apiParams = {
      page_number: params.page_no,
    };
    pageRef.current = params.page_no;

    if (allRowsRef.current[pageRef.current]) {
      setRows((prev) => ({
        ...prev,
        data: allRowsRef.current[pageRef.current],
        pagination: {
          ...prev.pagination,
          cur_page: params.page_no,
        },
      }));
    } else {
      fetchData(apiParams);
    }
  };
  const addRow = (row: any,index:number) => {
    const updatedRows = [...rows.data];
    updatedRows.splice(index + 1, 0, createEmptyBankFeesRow(defaultStatusValue));
    allRowsRef.current[currentPage] = updatedRows;
    setRows((prev) => ({
      ...prev,
      data: updatedRows,
    }));
  }
   const handleDeleteRow = async (type:number,index?: number,row?:BankFeesRow) => {
    const updatedDeleteConfirm = structuredClone(confirmation);  

    const updatedRows = structuredClone(rows.data);
    const updatedError = structuredClone(errors);
    if(type===DELETE_CONFIRMATION.SHOW && row && (index || index === 0)){
      if(row?.id){
        updatedDeleteConfirm.row = row;
        updatedDeleteConfirm.show = true;
        updatedDeleteConfirm.index = index;
      }else{
        
        if(updatedError?.[currentPage]?.[index]){
          delete updatedError?.[currentPage]?.[index]
          setErrors(updatedError);
        }
        updatedRows.splice(index, 1);
      }
    }
    if(type === DELETE_CONFIRMATION.NO){  
      updatedDeleteConfirm.show = false;
    }
    if(type === DELETE_CONFIRMATION.YES){
      try {
        setLoading(true);
        const response:any = await dispatch(deleteBankRow({id:updatedDeleteConfirm.row?.id}));   
        if(response?.status !== STATUS_SUCCESS) return;
        updatedRows.splice(updatedDeleteConfirm?.index, 1);
        toast.success(response?.message);
        updatedDeleteConfirm.show = false;
      } finally{
        setLoading(false);
      }
      
    }
  
    setConfirmation(updatedDeleteConfirm);

    setRows((prev) => ({
      ...prev,
      data: updatedRows,
    }));
  };
   
  const headers = headersData(translations)

  return (
    <React.Fragment>
      <FullPageLoader show={loading} />

      <div className="d-flex bank-fees">
          <div className="top-heading">
            <h1>{translations?.BANK?.HEADER}</h1>
          </div>

          <button
              type="button"
              className="btn-line"
              style={{ float: "right", margin: "5px" }}
              onClick={handleSubmit}
              disabled={loading}
          >
          {translations?.commonMsg?.save}
          </button>
      </div>

      <div className="data-table bank-config-outer confing-tab-table">
       

        <table>
          <thead>
            <tr key={'header'}>
              <th>{headers?.ONE}</th>
              <th>{headers?.TWO}</th>
              <th>{headers?.THREE}</th>
              <th>{headers?.FOUR}</th>
              <th>{headers?.FIVE}</th>
              <th>{headers?.SIX}</th>
              <th>{headers?.SEVEN}</th>
              <th>{headers?.EIGHT}</th>
              <th>{headers?.NINE}</th>
              <th>{headers?.TEN}</th>
              <th>{headers?.TWELVE}</th>


            </tr>
          </thead>
          <tbody>
            {rows?.data?.map((row, index) => {
              return (
                <>
                  <tr key={index}>
                    <td>
                      <fieldset className="single-select">
                        <div className="material">
                          <ReactSelect
                            options={partner}
                            // placeholder={translations?.BANK?.PARTNER_NAME}
                            placeholder=""
                            className="select-bx-table"
                            classNamePrefix="select-bx-table"
                            value={partner?.find(
                              (option: any) =>
                                +option.value === +row?.[KEYS.ONE],
                            )}
                            handleChange={(e: any) =>
                              handleChange(e, KEYS.ONE, index)
                            }
                            // isDisabled={true}
                          />
                          {errors?.[currentPage]?.[index]?.[KEYS.ONE] ||
                          errors?.[currentPage]?.[index]?.duplicate ? (
                            <span className="error-txt err-txt">
                              {errors?.[currentPage]?.[index]?.[KEYS.ONE] ||
                                errors?.[currentPage]?.[index]?.duplicate}
                            </span>
                          ) : (
                            ""
                          )}
                        </div>
                      </fieldset>
                    </td>

                    <td>
                      <fieldset className="single-select">
                        <div className="material">
                          <ReactSelect
                            options={insurerList?.filter(
                              (item: any) =>
                                item?.[KEYS.ONE] === row?.[KEYS.ONE],
                            )}
                            // placeholder={translations?.BANK?.INSURER_NAME}
                            placeholder=""
                            className="select-bx-table"
                            classNamePrefix="select-bx-table"
                            value={
                              row.insurer_id
                                ? insurerList
                                    ?.filter(
                                      (item: any) =>
                                        item?.[KEYS.ONE] === row?.[KEYS.ONE],
                                    )
                                    ?.find(
                                      (item: any) =>
                                        +item.value === +row?.[KEYS.TWO],
                                    )
                                : null
                            }
                            handleChange={(e: any) =>
                              handleChange(e, KEYS.TWO, index)
                            }
                            // isDisabled={true}
                          />
                          {errors?.[currentPage]?.[index]?.[KEYS.TWO] ? (
                            <span className="error-txt err-txt">
                              {errors?.[currentPage]?.[index]?.[KEYS.TWO]}
                            </span>
                          ) : (
                            ""
                          )}
                        </div>
                      </fieldset>
                    </td>

                    <td>
                      <fieldset className="single-select">
                        <div className="material">
                          <ReactSelect
                            options={paymentType}
                            // placeholder={headers?.THREE}
                            placeholder=""
                            className="select-bx-table"
                            classNamePrefix="select-bx-table"
                            value={paymentType?.find(
                              (option: any) =>
                                +option.value === +row?.[KEYS.THREE],
                            )}
                            handleChange={(e: any) =>
                              handleChange(e, KEYS.THREE, index)
                            }
                            // isDisabled={true}
                          />
                          {errors?.[currentPage]?.[index]?.[KEYS.THREE] ? (
                            <span className="error-txt err-txt">
                              {errors?.[currentPage]?.[index]?.[KEYS.THREE]}
                            </span>
                          ) : (
                            ""
                          )}
                        </div>
                      </fieldset>
                    </td>

                    <td>
                      <fieldset className="single-select">
                        <div className="material">
                          <ReactSelect
                            options={bankList}
                            // placeholder={headers?.FOUR}
                            placeholder=""
                            className="select-bx-table"
                            classNamePrefix="select-bx-table"
                            value={(Array.isArray(bankList) ? bankList : [])?.find(
                              (option: any) => +option.value === +row?.[KEYS.FOUR]
                            )}
                            handleChange={(e: any) =>
                              handleChange(e, KEYS.FOUR, index)
                            }
                            // isDisabled={true}
                          />
                          {errors?.[currentPage]?.[index]?.[KEYS.FOUR] ? (
                            <span className="error-txt err-txt">
                              {errors?.[currentPage]?.[index]?.[KEYS.FOUR]}
                            </span>
                          ) : (
                            ""
                          )}
                        </div>
                      </fieldset>
                    </td>

                    <td>
                      <fieldset>
                        <div className="material">
                          <input
                            type="number"
                            className="form-input"
                            placeholder=" "
                            value={row?.[KEYS?.FIVE]}
                            onChange={(e: any) =>
                              handleChange(e, KEYS?.FIVE, index)
                            }
                            onKeyDown={(e) => restrictInvalidNumberKeys(e)}
                            disabled={
                              row.insurer_status === INSURER_STATUS.INACTIVE
                                ? true
                                : false
                            }
                          />
                          {errors?.[currentPage]?.[index]?.[KEYS?.FIVE] ? (
                            <span className="error-txt err-txt">
                              {errors?.[currentPage]?.[index]?.[KEYS?.FIVE]}
                            </span>
                          ) : (
                            ""
                          )}
                        </div>
                      </fieldset>
                    </td>

                    <td>
                      <fieldset>
                        <div className="material">
                          <input
                            type="text"
                            className="form-input"
                            placeholder=" "
                            value={row?.[KEYS?.SIX]}
                            onChange={(e: any) =>
                              handleChange(e, KEYS?.SIX, index)
                            }
                            // onKeyDown={(e) => restrictInvalidNumberKeys(e)}
                            disabled={
                              row.insurer_status === INSURER_STATUS.INACTIVE
                                ? true
                                : false
                            }
                            onKeyDown={(e) => {
                              if (!ONLY_ALPHABET_REGEX.test(e.key) && e.key !== "Backspace") {
                                e.preventDefault();
                              }
                            }}
                          />
                          {errors?.[currentPage]?.[index]?.[KEYS?.SIX] ? (
                            <span className="error-txt err-txt">
                              {errors?.[currentPage]?.[index]?.[KEYS?.SIX]}
                            </span>
                          ) : (
                            ""
                          )}
                        </div>
                      </fieldset>
                    </td>

                    <td>
                      <fieldset>
                        <div className="material">
                          <input
                            type="number"
                            className="form-input"
                            placeholder=" "
                            value={row?.[KEYS?.SEVEN]}
                            onChange={(e: any) =>
                              handleChange(e, KEYS?.SEVEN, index)
                            }
                            onKeyDown={(e) => restrictInvalidNumberKeys(e)}
                            disabled={
                              row.insurer_status === INSURER_STATUS.INACTIVE
                                ? true
                                : false
                            }
                          />
                          {errors?.[currentPage]?.[index]?.[KEYS?.SEVEN] ? (
                            <span className="error-txt err-txt">
                              {errors?.[currentPage]?.[index]?.[KEYS?.SEVEN]}
                            </span>
                          ) : (
                            ""
                          )}
                        </div>
                      </fieldset>
                    </td>

                    <td>
                      <fieldset>
                        <div className="material">
                          <input
                            type="number"
                            className="form-input"
                            placeholder=" "
                            value={row?.[KEYS?.EIGHT]}
                            onChange={(e: any) =>
                              handleChange(e, KEYS?.EIGHT, index)
                            }
                            onKeyDown={(e) => restrictInvalidNumberKeys(e)}
                            disabled={
                              row.insurer_status === INSURER_STATUS.INACTIVE
                                ? true
                                : false
                            }
                          />
                          {errors?.[currentPage]?.[index]?.[KEYS?.EIGHT] ? (
                            <span className="error-txt err-txt">
                              {errors?.[currentPage]?.[index]?.[KEYS?.EIGHT]}
                            </span>
                          ) : (
                            ""
                          )}
                        </div>
                      </fieldset>
                    </td>

                    <td>
                      <fieldset>
                        <div className="material">
                          <input
                            type="text"
                            className="form-input"
                            placeholder=""
                            value={row?.[KEYS?.NINE]}
                            onChange={(e: any) =>
                              handleChange(e, KEYS?.NINE, index)
                            }
                            onKeyDown={(e) =>
                              restrictInvalidNumberKeys(e, ["."])
                            }
                            disabled={
                              row.insurer_status === INSURER_STATUS.INACTIVE
                                ? true
                                : false
                            }
                          />
                          {errors?.[currentPage]?.[index]?.[KEYS?.NINE] ? (
                            <span className="error-txt err-txt">
                              {errors?.[currentPage]?.[index]?.[KEYS?.NINE]}
                            </span>
                          ) : (
                            ""
                          )}
                        </div>
                      </fieldset>
                    </td>

                    <td>
                      <fieldset>
                        <div className="material">
                          <input
                            type="text"
                            className="form-input"
                            placeholder=" "
                            value={HELPER.addCurrencyFormatting(
                              row?.[KEYS?.TEN],
                            )}
                            onChange={(e: any) =>
                              handleChange(e, KEYS?.TEN, index)
                            }
                            onKeyDown={(e) => restrictInvalidNumberKeys(e)}
                            disabled={
                              row.insurer_status === INSURER_STATUS.INACTIVE
                                ? true
                                : false
                            }
                          />
                          {errors?.[currentPage]?.[index]?.[KEYS?.TEN] ? (
                            <span className="error-txt err-txt">
                              {errors?.[currentPage]?.[index]?.[KEYS?.TEN]}
                            </span>
                          ) : (
                            ""
                          )}
                        </div>
                      </fieldset>
                    </td>
                    
                    <td>
                      {/* <ul className="action-btn">
                        <li
                          onClick={() =>
                            handleDeleteRow(
                              DELETE_CONFIRMATION.SHOW,
                              index,
                              row,
                            )
                          }
                          className="delete-box"
                        >
                          <i className="ic-delete-icn delete-btn"></i>
                        </li>
                      </ul> */}
                      <fieldset className="single-select">
                        <div className="material">
                          <ReactSelect
                            options={configStatus}
                            // placeholder={headers?.TWELVE}
                            placeholder=""
                            className="select-bx-table"
                            classNamePrefix="select-bx-table"
                            value={(Array.isArray(configStatus) ? configStatus : [])?.find(
                              (option: any) => +option.value === +row?.[KEYS.TWELVE]
                            )}
                            handleChange={(e: any) =>
                              handleChange(e, KEYS.TWELVE, index)
                            }
                            isDisabled={
                              row.insurer_status === INSURER_STATUS.INACTIVE
                                ? true
                                : false
                            }
                          />
                          {errors?.[currentPage]?.[index]?.[KEYS.TWELVE] ? (
                            <span className="error-txt err-txt">
                              {errors?.[currentPage]?.[index]?.[KEYS.TWELVE]}
                            </span>
                          ) : (
                            ""
                          )}
                        </div>
                      </fieldset>
                    </td>
                    
                  </tr>
                  {rows?.data?.length - 1 === index && (
                    <tr key={"add-row"}>
                      <td className="p-tb-0" colSpan={7}>
                        <a
                          href="#!"
                          onClick={(e) => {
                            e.preventDefault();
                            addRow(row, index);
                          }}
                        >
                          <i className="ic-add m-xs-r"></i>{" "}
                          {translations?.BANK?.ADD_ROW}
                        </a>
                      </td>
                    </tr>
                  )}
                </>
              );})}
          </tbody>
        </table>
      </div>
      {confirmation?.show && (
        <ConfirmPopup
          confirmNo={() => handleDeleteRow(DELETE_CONFIRMATION.NO)}
          confirmYes={() => handleDeleteRow(DELETE_CONFIRMATION.YES)}
          message={translations?.commonMsg?.DELETE_CONFIRMATION}
        />
      )}
      <div className="pagination-outer">
        {rows && rows?.pagination?.total ? (
                <Pagination
                    paginateData={{
                        page: currentPage,
                        loading,
                        tableData: fetchData,
                        paginationData: rows.pagination,
                    }}
                    sendRequestDataToParent={handlePageChange}
                />
            ) : (
                ""
            )}
      </div>
    </React.Fragment>
  );
}
export default React.memo(Bank)
